var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useRef, useState } from "react";
import Select, { components } from "react-select";
import { Box, InputAdornment, makeStyles } from "@material-ui/core";
import { FTLTextField } from "../FTLTextField";
import { useClickOutside } from "ftl-dashboards-core";
import { CustomValueContainer } from "./CustomValueContainer";
import { SearchIcon, ClearIcon, ChevronIcon } from "../icons";
import { InfiniteLoader } from "./InfiniteLoader";
import { FTLLabel } from "../FTLLabel";
import { FTLIconButton } from "../FTLIconButton";
import { FTLCheckbox } from "../FTLCheckbox";
import { FTLTypography } from "../FTLTypography";
import { defaultSelectStyles } from ".";
import { CloseIcon } from "../icons/Close";
import { FTLDropdownMenu } from "../FTLDropdownMenu/FTLDropdownMenu";
const useStyles = makeStyles((theme) => ({
    option: {
        color: "rgb(19, 35, 57)",
        cursor: "pointer",
        fontSize: "14px",
        padding: "8px 12px",
        userSelect: "none",
        boxSizing: "border-box",
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: "-0.09px",
        "&:hover": {
            background: "rgba(3, 10, 22, 0.04)",
        },
    },
    smallIcon: {
        width: "20px",
        height: "20px",
    },
}));
export function FTLBasicSelect(_a) {
    var { options, label, isError, isDisabled, onChange, isSearchable = false, isSearchInList = false, isClearable = false, type = "default", searchPlaceholder = "Поиск", required, components: CustomComponents, value, size = "default", width = "default", menuWidth, placeholder = "Не выбрано", itemSize = 32, records = 4, isInfinite, noOptionsMessage = () => "Нет данных", onClick, isMulti, BoxProps, withCheckboxes, hideSelectedOptions = false, popperPlacement = "bottom-start", showEmptyMenu = false, SelectComponent = Select } = _a, otherProps = __rest(_a, ["options", "label", "isError", "isDisabled", "onChange", "isSearchable", "isSearchInList", "isClearable", "type", "searchPlaceholder", "required", "components", "value", "size", "width", "menuWidth", "placeholder", "itemSize", "records", "isInfinite", "noOptionsMessage", "onClick", "isMulti", "BoxProps", "withCheckboxes", "hideSelectedOptions", "popperPlacement", "showEmptyMenu", "SelectComponent"]);
    const classes = useStyles();
    const containerRef = useRef(null);
    const popperRef = useRef(null);
    const controlRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const handleClickOutside = useCallback((e) => {
        if (controlRef.current !== e.target)
            setIsMenuOpen(false);
    }, []);
    useClickOutside(popperRef, handleClickOutside);
    return (React.createElement(Box, Object.assign({ width: width === "small" ? 160 : "100%", minWidth: width === "small" ? 160 : 0 }, BoxProps),
        label && (React.createElement(FTLLabel, { label: label, required: required, whiteSpace: "nowrap" })),
        React.createElement(SelectComponent, Object.assign({ options: options, menuIsOpen: isMenuOpen, hideSelectedOptions: hideSelectedOptions, isSearchable: isSearchable && !isSearchInList, isClearable: isClearable, isMulti: isMulti, isError: isError, isDisabled: isDisabled, isFocused: isMenuOpen, placeholder: placeholder, value: value, onChange: (value, meta) => {
                if (meta.action === "clear" ||
                    meta.action === "remove-value" ||
                    !isMulti)
                    setIsMenuOpen(false);
                onChange &&
                    onChange(value, meta);
            }, 
            // input
            components: Object.assign(Object.assign({}, CustomComponents), { Control: (props) => {
                    return (React.createElement(components.Control, Object.assign({}, props, { innerProps: {
                            onMouseDown: (e) => {
                                props.innerProps.onMouseDown &&
                                    props.innerProps.onMouseDown(e);
                                if (e.button === 0 &&
                                    !isDisabled &&
                                    (((options === null || options === void 0 ? void 0 : options.length) && !showEmptyMenu) || showEmptyMenu)) {
                                    controlRef.current = e.target;
                                    setIsMenuOpen((val) => !val);
                                }
                            },
                        } })));
                }, Menu: (_a) => {
                    var { children } = _a, otherProps = __rest(_a, ["children"]);
                    return (React.createElement(FTLDropdownMenu, { ref: popperRef, open: true, containerRef: containerRef, placement: popperPlacement, menuWidth: menuWidth },
                        isSearchable && isSearchInList && (React.createElement(FTLTextField, { variant: "standard", autoCorrect: "off", autoComplete: "off", autoFocus: true, fullWidth: true, spellCheck: "false", onMouseDown: (e) => {
                                e.stopPropagation();
                            }, onTouchStart: (e) => {
                                e.stopPropagation();
                            }, style: { marginBottom: 8 }, InputProps: {
                                style: { paddingLeft: 4 },
                                endAdornment: (React.createElement(InputAdornment, { position: "end" }, otherProps.selectProps.inputValue ? (React.createElement(FTLIconButton, { onClick: () => {
                                        if (otherProps.selectProps.onInputChange) {
                                            otherProps.selectProps.onInputChange("", {
                                                action: "input-change",
                                            });
                                        }
                                    }, tabIndex: -1, disabled: isDisabled },
                                    React.createElement(ClearIcon, null))) : (React.createElement(FTLIconButton, { tabIndex: -1, disabled: isDisabled },
                                    React.createElement(SearchIcon, null))))),
                            }, value: otherProps.selectProps.inputValue, onChange: (e) => {
                                if (otherProps.selectProps.onInputChange) {
                                    otherProps.selectProps.onInputChange(e.currentTarget.value, {
                                        action: "input-change",
                                    });
                                }
                            }, placeholder: searchPlaceholder })),
                        children));
                }, MenuList: (props) => {
                    const { children } = props, otherProps = __rest(props, ["children"]);
                    return isInfinite ? (React.createElement(InfiniteLoader, { type: "sync", itemSize: itemSize, queryValue: props.selectProps.inputValue, items: React.Children.map(children, (item) => { var _a; return (_a = item === null || item === void 0 ? void 0 : item.props) === null || _a === void 0 ? void 0 : _a.data; }), hasNextPage: props.selectProps.options &&
                            props.selectProps.options.length > 0, loadItems: (startIndex, stopIndex) => {
                            let arr = props.selectProps.options || [];
                            if (props.selectProps.inputValue)
                                arr = arr.filter((option) => option.label.match(new RegExp(props.selectProps.inputValue, "gmi")));
                            return {
                                items: arr.slice(startIndex, stopIndex + 1),
                                count: arr.length,
                            };
                        }, records: records, itemCount: props.selectProps.options.length, itemRenderer: ({ index, style, data }) => {
                            return (React.createElement(Box, { display: "flex", justifyContent: "space-between", onMouseDown: (e) => e.button === 0 &&
                                    data.value &&
                                    props.selectOption(data), onTouchStart: (e) => data.value && props.selectOption(data), className: classes.option, style: Object.assign({}, style) }, data && data.meta ? (React.createElement(React.Fragment, null,
                                React.createElement("span", null, data.label),
                                React.createElement(FTLTypography, { color: "tertiary", variant: "listItemText" }, data.meta))) : (data === null || data === void 0 ? void 0 : data.label)));
                        } })) : (React.createElement(components.MenuList, Object.assign({}, otherProps), children));
                }, DropdownIndicator: (props) => {
                    return type === "default" ||
                        (type === "filter" && !props.hasValue) ||
                        !isClearable ? (React.createElement(components.DropdownIndicator, Object.assign({}, props, { innerProps: {
                            onClick: (e) => {
                                if (e.button === 0) {
                                    !isDisabled && (options === null || options === void 0 ? void 0 : options.length) && setIsMenuOpen(true);
                                }
                            },
                        } }),
                        React.createElement(ChevronIcon, { size: size }))) : null;
                }, MultiValueRemove: (props) => {
                    return (React.createElement(components.MultiValueRemove, Object.assign({}, props),
                        React.createElement(CloseIcon, { size: "small" })));
                }, IndicatorSeparator: () => null, ClearIndicator: (props) => {
                    return (React.createElement(components.ClearIndicator, Object.assign({}, props),
                        React.createElement(ClearIcon, { className: size === "small" ? classes.smallIcon : "" })));
                }, Option: (props) => {
                    return (React.createElement("div", { onMouseDown: (e) => (withCheckboxes || e.bubbles === false) &&
                            e.button === 0 &&
                            props.data.value &&
                            props.selectOption(props.data), onTouchStart: (e) => props.data.value && props.selectOption(props.data) },
                        React.createElement(components.Option, Object.assign({}, props),
                            React.createElement(Box, { display: "flex", justifyContent: "space-between" },
                                withCheckboxes ? (React.createElement(FTLCheckbox, { label: props.data.label, checked: props.isSelected })) : (React.createElement("span", null, props.data.label)),
                                props.data.meta && (React.createElement(FTLTypography, { color: "tertiary", variant: "listItemText" }, props.data.meta))))));
                }, ValueContainer: (props) => {
                    if (isSearchable && isSearchInList)
                        return React.createElement(CustomValueContainer, Object.assign({}, props));
                    else
                        return React.createElement(components.ValueContainer, Object.assign({}, props));
                } }), noOptionsMessage: noOptionsMessage, styles: defaultSelectStyles(size, isError, isMulti, isMenuOpen, isDisabled, withCheckboxes) }, otherProps)),
        React.createElement("div", { ref: containerRef, onClick: onClick })));
}
//# sourceMappingURL=FTLBasicSelect.js.map